import type React from "react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils/tailwind";

interface TextAreaProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	label?: string;
	error?: string;
	labelClassName?: string;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
	({ label, error, id, className, labelClassName, ...props }, ref) => {
		const textareaId =
			id ||
			(label
				? `textarea-${label.replace(/\s+/g, "-").toLowerCase()}`
				: undefined);

		return (
			<div className="relative">
				{label && (
					<label
						htmlFor={textareaId}
						className={cn(
							"block text-[16px] font-medium font-rubik mb-6 capitalize leading-[136%] tracking-[0.24px] select-none",
							error ? "text-system-red-500" : "text-grayscale-950",
							labelClassName,
						)}
					>
						{label}
					</label>
				)}
				<textarea
					ref={ref}
					id={textareaId}
					className={cn(
						"w-full rounded-16 border bg-grayscale-white px-12 py-8 text-sm text-grayscale-800 transition-[border-color] duration-150 ease-in-out focus:outline-none placeholder:text-grayscale-500 placeholder:capitalize resize-y",
						error
							? "border-system-red-500"
							: "border-grayscale-300 focus:border-grayscale-400",
						className,
					)}
					aria-invalid={!!error}
					{...props}
				/>
				{error && (
					<span className="block text-xs text-system-red-500 mt-4 pl-12">
						{error}
					</span>
				)}
			</div>
		);
	},
);

TextArea.displayName = "TextArea";

export default TextArea;
